import type { Appointment, OpeningWindow, TimeBlock, TimeOff } from '../api/client'
import { addDays, weekStart } from './dates'
import { bookedSummary, onTheBook, timeOffOn } from './dayRows'
import { clientName, timeRange } from './names'
import { NO_CATEGORY_COLOUR, WEEKDAYS, minutesToTime } from '../catalog/format'

export interface WeekBlock {
  id: string
  top: number // fraction of the column's height
  height: number
  blockedHeight: number // fraction of the column's height, 0 when none
  colour: string
  held: boolean // waiting for its deposit
  // What the block says: from 768px its time, the client's whole name and
  // the treatment, as far as its height allows; initials on a phone; the
  // whole thing for screen readers.
  start: string // "09:30"
  time: string // "09:30–10:30"
  name: string
  treatment: string
  initials: string
  label: string
  // short is under 45 minutes: time and name share one line.
  short: boolean
  // done is completed: drawn grey, so what's still open stands out.
  done: boolean
  noShow: boolean
}

// A time block drawn in a column, under any booking.
export interface WeekTimeBlock {
  block: TimeBlock
  top: number // fraction of the column's height
  height: number
  text: string // what it says: her label, or "Blocked"
  label: string // for screen readers: "Blocked, Dentist, 14:30 to 15:30"
}

// A stretch of a column outside her hours: a break, or before/after work.
export interface WeekOff {
  top: number
  height: number
  kind: 'break' | 'closed'
}

export interface WeekColumn {
  date: string
  weekday: string // "Monday"
  dayOfMonth: number
  closed: boolean
  timeOff: boolean
  isToday: boolean
  blocks: WeekBlock[]
  timeBlocks: WeekTimeBlock[]
  // count is the day's appointments on the book (no-shows are drawn, not counted).
  count: number
  off: WeekOff[]
}

export interface WeekGrid {
  from: string
  to: string
  startMinute: number
  endMinute: number
  hours: number[]
  columns: WeekColumn[]
  count: number
  bookedPence: number
}

// What a block's label adds after the treatment; an open booking adds nothing.
const STATUS_WORDS: Record<string, string> = { completed: ', completed', no_show: ', no-show' }
const defaultStart = 9 * 60
const defaultEnd = 18 * 60
// Consultation's colour, for bookings whose service was deleted.
const fallbackColour = NO_CATEGORY_COLOUR

// weekGrid lays out the Monday-to-Sunday week containing date. The vertical
// range runs from the earliest working start to the latest working end, on
// whole hours, widened to fit any booking or time block outside it.
export function weekGrid(
  date: string,
  windows: OpeningWindow[],
  timeOff: TimeOff[],
  appointments: Appointment[],
  today: string,
  timeBlocks: TimeBlock[] = [],
): WeekGrid {
  const from = weekStart(date)
  const to = addDays(from, 6)
  const bookings = appointments
    .filter((a) => a.status !== 'cancelled' && a.date >= from && a.date <= to)
    .sort((a, b) => a.start_minute - b.start_minute)

  let start = windows.length === 0 ? defaultStart : Math.min(...windows.map((w) => w.start_minute))
  let end = windows.length === 0 ? defaultEnd : Math.max(...windows.map((w) => w.end_minute))
  for (const b of bookings) {
    start = Math.min(start, b.start_minute)
    end = Math.max(end, Math.min(b.blocked_until_minute, 1440))
  }
  const blocks = timeBlocks.filter((b) => b.date >= from && b.date <= to).sort((a, b) => a.start_minute - b.start_minute)
  for (const b of blocks) {
    start = Math.min(start, b.start_minute)
    end = Math.max(end, b.end_minute)
  }
  start = Math.floor(start / 60) * 60
  end = Math.ceil(end / 60) * 60
  const span = end - start

  const hours: number[] = []
  for (let m = start; m <= end; m += 60) {
    hours.push(m / 60)
  }

  const columns = WEEKDAYS.map((weekday, i): WeekColumn => {
    const day = addDays(from, i)
    return {
      date: day,
      weekday,
      dayOfMonth: Number(day.slice(8)),
      closed: !windows.some((w) => w.weekday === i),
      timeOff: timeOffOn(day, timeOff) !== null,
      isToday: day === today,
      count: bookings.filter((b) => b.date === day && onTheBook(b)).length,
      blocks: bookings
        .filter((b) => b.date === day)
        .map((b) => ({
          id: b.id,
          top: (b.start_minute - start) / span,
          height: (b.end_minute - b.start_minute) / span,
          blockedHeight: (Math.min(b.blocked_until_minute, 1440) - b.end_minute) / span,
          colour: b.service.colour ?? fallbackColour,
          held: b.awaiting_deposit,
          start: minutesToTime(b.start_minute),
          time: timeRange(b),
          name: clientName(b.client),
          treatment: b.service.name,
          initials: initialsOf(b.client),
          label: `${minutesToTime(b.start_minute)} ${clientName(b.client)}, ${b.service.name}${STATUS_WORDS[b.status] ?? ''}`,
          short: b.end_minute - b.start_minute < 45,
          done: b.status === 'completed',
          noShow: b.status === 'no_show',
        })),
      timeBlocks: blocks
        .filter((b) => b.date === day)
        .map((b) => ({
          block: b,
          top: (b.start_minute - start) / span,
          height: (b.end_minute - b.start_minute) / span,
          text: b.label ?? 'Blocked',
          label: `Blocked, ${b.label ? `${b.label}, ` : ''}${minutesToTime(b.start_minute)} to ${minutesToTime(b.end_minute)}`,
        })),
      off: offStretches(windows.filter((w) => w.weekday === i), start, end),
    }
  })

  return {
    from,
    to,
    startMinute: start,
    endMinute: end,
    hours,
    columns,
    ...bookedSummary(bookings),
  }
}

// tapMinute is the minute a tap on a column's empty space means, as a quarter
// hour, for fraction of the way down the grid. It is null where nothing can be
// started: a closed day, time off, her break or hours before and after work,
// or a booking and the buffer tail after it (those are busy, so the tap opens
// the day instead). It never reaches the last quarter hour of the grid or the
// day. A tap that rounds up to the start of a break or a booking steps back to
// the quarter hour before it; a rounded minute that still falls in a closed or
// busy stretch gives null.
export function tapMinute(col: WeekColumn, grid: WeekGrid, fraction: number): number | null {
  const busyAt = (f: number) => col.blocks.some((b) => b.top <= f && f < b.top + b.height + b.blockedHeight)
  if (col.closed || col.timeOff || col.off.some((o) => o.top <= fraction && fraction < o.top + o.height) || busyAt(fraction)) {
    return null
  }
  const span = grid.endMinute - grid.startMinute
  const at = (f: number) => Math.round(grid.startMinute + f * span)
  const rounded = Math.round((grid.startMinute + fraction * span) / 15) * 15
  const minute = Math.max(grid.startMinute, Math.min(rounded, grid.endMinute - 15, 1425))
  const atStart = col.off.some((o) => at(o.top) === minute) || col.blocks.some((b) => at(b.top) === minute)
  const stepped = atStart ? Math.max(grid.startMinute, minute - 15) : minute
  // Hours that don't start on a quarter hour, or a booking that doesn't, can
  // leave the rounded minute (or the step back) inside a closed or busy
  // stretch: that is not open space either.
  const inOff = col.off.some((o) => at(o.top) <= stepped && stepped < at(o.top + o.height))
  const inBusy = col.blocks.some((b) => at(b.top) <= stepped && stepped < at(b.top + b.height + b.blockedHeight))
  return inOff || inBusy ? null : stepped
}

function initialsOf(client: { first_name: string; last_name: string | null } | null): string {
  if (!client) return '–'
  return `${client.first_name.charAt(0)}${client.last_name?.charAt(0) ?? ''}`.toUpperCase()
}

// offStretches is where a day's column sits outside her hours, between start
// and end: before work, breaks between windows, and after work. A day with
// no windows is left to the column's own closed shading.
export function offStretches(dayWindows: OpeningWindow[], start: number, end: number): WeekOff[] {
  if (dayWindows.length === 0) return []
  const span = end - start
  const sorted = [...dayWindows].sort((a, b) => a.start_minute - b.start_minute)
  const off: WeekOff[] = []
  const add = (from: number, to: number, kind: WeekOff['kind']) => {
    const a = Math.max(from, start)
    const b = Math.min(to, end)
    if (b > a) off.push({ top: (a - start) / span, height: (b - a) / span, kind })
  }
  add(start, sorted[0].start_minute, 'closed')
  for (let i = 1; i < sorted.length; i++) add(sorted[i - 1].end_minute, sorted[i].start_minute, 'break')
  add(sorted[sorted.length - 1].end_minute, end, 'closed')
  return off
}
